<script setup>
// Linhas da tabela de funcoes personalizadas.
//
// A versao anterior imprimia TODAS as permissoes como um paragrafo de texto
// corrido — a funcao ADM virava uma parede de 20 linhas e o total sumia. Agora
// a linha mostra a CONTAGEM; clicar expande os selos, um por permissao (mesmo
// gesto das etapas na lista de pipelines).
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { getI18nKey } from 'dashboard/routes/dashboard/settings/helper/settingsHelper';

import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  roles: {
    type: Array,
    required: true,
  },
  loading: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['edit', 'delete']);

const { t } = useI18n();

const expanded = ref(new Set());
const toggleExpanded = id => {
  if (expanded.value.has(id)) expanded.value.delete(id);
  else expanded.value.add(id);
};

const permissionLabel = permission =>
  t(getI18nKey('CUSTOM_ROLE.PERMISSIONS', permission));
</script>

<template>
  <tbody>
    <template v-for="customRole in roles" :key="customRole.id">
      <tr
        class="border-b border-n-weak last:border-b-0 hover:bg-n-solid-2 cursor-pointer"
        @click="toggleExpanded(customRole.id)"
      >
        <td class="px-4 py-3 first:pl-6">
          <b
            class="block text-[13px] font-semibold text-n-slate-12"
            :title="customRole.name"
          >
            {{ customRole.name }}
          </b>
          <span
            v-if="customRole.description"
            class="block max-w-[52ch] text-xs text-n-slate-10"
          >
            {{ customRole.description }}
          </span>
        </td>
        <td class="px-4 py-3">
          <span
            class="inline-flex items-center gap-1.5 rounded border border-n-weak bg-n-alpha-1 px-2 py-0.5 text-[11.5px] text-n-slate-11"
          >
            <b class="tabular-nums">{{ customRole.permissions.length }}</b>
            {{ $t('CUSTOM_ROLE.LIST.TABLE_HEADER.PERMISSIONS').toLowerCase() }}
            <span
              class="i-lucide-chevron-down size-3 text-n-slate-9 transition-transform"
              :class="{ '-rotate-90': !expanded.has(customRole.id) }"
            />
          </span>
        </td>
        <td class="px-4 py-3 pr-6">
          <div class="flex justify-end gap-1">
            <Button
              v-tooltip.top="$t('CUSTOM_ROLE.EDIT.BUTTON_TEXT')"
              icon="i-lucide-pen"
              slate
              xs
              faded
              @click.stop="emit('edit', customRole)"
            />
            <Button
              v-tooltip.top="$t('CUSTOM_ROLE.DELETE.BUTTON_TEXT')"
              icon="i-lucide-trash-2"
              xs
              ruby
              faded
              :is-loading="loading[customRole.id]"
              @click.stop="emit('delete', customRole)"
            />
          </div>
        </td>
      </tr>
      <tr v-if="expanded.has(customRole.id)">
        <td colspan="3" class="border-b border-n-weak bg-n-solid-2 px-6 py-3">
          <div class="flex flex-wrap gap-1.5">
            <span
              v-for="permission in customRole.permissions"
              :key="permission"
              class="rounded-md border border-n-weak bg-n-solid-1 px-2 py-0.5 text-[11px] text-n-slate-11"
            >
              {{ permissionLabel(permission) }}
            </span>
          </div>
        </td>
      </tr>
    </template>
  </tbody>
</template>
